import React, { useState } from 'react';

import {
	AspectRatio,
	Flex,
	HStack,
	SimpleGrid,
	Stack,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	Text,
	VStack,
} from '@chakra-ui/react';
import { cn } from '@easydatasolution/ui-react/lib/utils';
import { AnimatePresence } from 'framer-motion';
import { Link as GatsbyLink } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { createId } from '../../misc/utilities';
import { Accordion } from '../Accordion/Accordion';
import { Button } from '../Button/Button';
import { ContactLinks } from '../ContactLinks/ContactLinks';
import { DiamondNumber } from '../DiamondNumber';
import { Grid } from '../Grid/Grid';
import { Heading } from '../Heading/Heading';
import { IconCard } from '../IconCard/IconCard';
import { ImageCard } from '../ImageCard';
import { Link } from '../Link/Link';
import { Markdown } from '../Markdown/Markdown';
import { MarkdownRaw } from '../MarkdownRaw';
import { ReviewsCarousel } from '../ReviewsCarousel';
import { Section } from '../Section/Section';
import { SectionNew } from '../SectionNew';
import Steps from '../Steps';

const transformBackgroundColor = (backgroundColor) =>
	backgroundColor === 'gray' ? 'secondary.50' : 'white';

const Wrapper = ({ heading, intro, children, ...rest }) => {
	return (
		<Section py={['40px', '60px', '80px', '100px']} id={createId(heading)} {...rest}>
			<Heading as="h2" textAlign="center" fontWeight="bold" letterSpacing={0} color="secondary.700">
				{heading}
			</Heading>
			{intro && (
				<Markdown
					content={intro}
					mt={5}
					mx="auto"
					whiteSpace="pre-wrap"
					textAlign="center"
					fontSize="xl"
					color="secondary.700"
					maxW={{ base: 'initial', lg: '80%' }}
				/>
			)}
			{children}
		</Section>
	);
};

const TextSection = (section) => {
	const [isExpanded, setIsExpanded] = useState(false);

	const { title, intro, text, links, backgroundColor } = section;

	return (
		<Wrapper heading={title} intro={intro} bg={transformBackgroundColor(backgroundColor)}>
			{((links && links.length > 0) || text) && (
				<Stack
					mt={5}
					spacing={{ base: 2, lg: 6 }}
					direction={{ base: 'column', lg: 'row' }}
					alignItems="center"
					justifyContent="center"
				>
					{(links ?? []).map((link, idx) => (
						<Link key={idx} to={link.urlPath} fontSize="lg">
							{link.title}
						</Link>
					))}
				</Stack>
			)}
			{text && (
				<Button
					display="block"
					mx="auto"
					mt={8}
					variant={'outline'}
					onClick={() => setIsExpanded((prev) => !prev)}
				>
					{isExpanded ? 'Weniger anzeigen' : 'Weiterlesen'}
				</Button>
			)}
			<AnimatePresence>
				{text && isExpanded && (
					<Markdown
						content={text}
						maxW="1000px"
						mx="auto"
						initial={{ opacity: 0, height: 0, paddingTop: '0px' }}
						animate={{
							opacity: 1,
							height: 'auto',
							paddingTop: '20px',
						}}
						exit={{ opacity: 0, height: 0, paddingTop: '0px' }}
						transition={{
							opacity: { duration: 0.3 },
							height: { ease: 'easeIn' },
						}}
					/>
				)}
			</AnimatePresence>
		</Wrapper>
	);
};

export const DynamicSection = ({ section }) => {
	switch (section.type) {
		case 'teaser': {
			const { title, subtitle, link } = section;
			return (
				<Section
					pt={['40px', '60px', '80px', '100px']}
					pb={['40px', '60px', '80px', '100px']}
					id={createId(title)}
				>
					<HStack
						mx="auto"
						my={3}
						px={8}
						py={10}
						spacing={8}
						bg="secondary.50"
						justifyContent="space-between"
					>
						<Stack direction="column" spacing={1}>
							<Text fontSize="xl" fontWeight="bold" className="text-pretty" color="secondary.700">
								{title}
							</Text>
							{subtitle && (
								<Text fontSize="xl" color="secondary.700">
									{subtitle}
								</Text>
							)}
						</Stack>
						{link?.title && link?.urlPath && (
							<Button display="flex" as={GatsbyLink} to={link.urlPath} flexShrink={0}>
								{link.title}
							</Button>
						)}
					</HStack>
				</Section>
			);
		}
		case 'contactTeaser': {
			const { title } = section;
			return (
				<Section
					pt={['40px', '60px', '80px', '100px']}
					pb={['40px', '60px', '80px', '100px']}
					id={createId(title)}
				>
					<VStack
						mx="auto"
						my={3}
						px={8}
						py={10}
						spacing={8}
						bg="secondary.50"
						justifyContent="space-between"
					>
						<Text fontSize="2xl" fontWeight="bold" color="secondary.700">
							{title}
						</Text>
						<ContactLinks w="100%" />
					</VStack>
				</Section>
			);
		}
		case 'text': {
			return <TextSection {...section} />;
		}
		case 'longText': {
			const { title, text } = section;
			return (
				<SectionNew title={title} isCentered={true} className={'py-16'} id={createId(title)}>
					<MarkdownRaw
						className={
							'prose-xl mt-5 max-w-none text-secondary-700 prose-headings:text-secondary-700 prose-a:text-primary-500 prose-a:hover:text-primary-700 prose-blockquote:text-secondary-700 prose-strong:text-secondary-700'
						}
					>
						{text}
					</MarkdownRaw>
				</SectionNew>
			);
		}
		case 'plainCards': {
			const { title, subtitle, intro, horizontal, cards } = section;
			return (
				<SectionNew
					title={title}
					subtitle={subtitle}
					intro={intro}
					className={'py-16'}
					id={createId(title)}
				>
					<div
						className={cn(
							'mt-12 flex',
							horizontal && 'grid grid-cols-1 gap-16 xl:grid-cols-3',
							!horizontal && 'flex-col gap-8',
						)}
					>
						{cards.map((card, idx) => (
							<article key={idx} className="flex flex-col gap-4 rounded-lg px-5 py-8 shadow-md">
								<h3 className="text-xl font-semibold text-secondary-700">{card.title}</h3>

								<MarkdownRaw
									className={cn(
										'prose-lg mx-auto text-secondary-700 prose-strong:text-secondary-700',
										horizontal && 'grow',
									)}
								>
									{card.text}
								</MarkdownRaw>
								{card.link && (
									<Button
										as={GatsbyLink}
										key={idx}
										to={card.link.urlPath}
										variant={'outline'}
										className="mt-4 self-start"
									>
										{card.link.title}
									</Button>
								)}
							</article>
						))}
					</div>
				</SectionNew>
			);
		}
		case 'iconCards': {
			const { title, intro, cards } = section;
			return (
				<Wrapper heading={title} intro={intro}>
					<SimpleGrid
						columns={{ base: 1, lg: 3 }}
						gap={12}
						placeItems={{ base: 'center', lg: 'initial' }}
						mt={8}
					>
						{cards.map(({ icon, title, text, link }, idx) => (
							<IconCard
								key={idx}
								iconName={icon}
								title={title}
								text={text}
								link={link}
								mt={8}
								maxW="420px"
							/>
						))}
					</SimpleGrid>
				</Wrapper>
			);
		}
		case 'imageCards': {
			const { title, intro, cards } = section;
			const cardCount = cards.length;
			// prevents whitespace to the side of the cards
			const getGridClass = (count) => {
				if (count === 1) return '';
				if (count % 2 === 0) return 'lg:gap-12 lg:grid-cols-2 2xl:gap-6 2xl:grid-cols-4'; // Even number
				return '2xl:gap-12 lg:grid-cols-3'; // Default for odd numbers
			};

			return (
				<Wrapper heading={title} intro={intro}>
					<div className={`mx-auto mt-8 grid w-full grid-cols-1 gap-6 ${getGridClass(cardCount)}`}>
						{cards.map(({ title, text, image }, idx) => (
							<ImageCard
								key={idx}
								title={title}
								text={text}
								image={image}
								className="h-full w-full"
							/>
						))}
					</div>
				</Wrapper>
			);
		}
		case 'accordion': {
			const { title, intro, items, backgroundColor } = section;
			return (
				<Wrapper heading={title} intro={intro} bg={transformBackgroundColor(backgroundColor)}>
					<Accordion items={items} mt={12} />
				</Wrapper>
			);
		}
		case 'grid': {
			const { title, intro, gridTitle, maxColumns, gridItems, link, backgroundColor } = section;
			return (
				<Wrapper heading={title} intro={intro} bg={transformBackgroundColor(backgroundColor)}>
					<Flex mt={16} direction="column" alignItems="center">
						{gridTitle && (
							<Heading
								as="h3"
								textAlign="center"
								fontSize="xl"
								fontWeight="bold"
								letterSpacing={0}
								color="secondary.700"
							>
								{gridTitle}
							</Heading>
						)}
						<Grid maxColumns={maxColumns} items={gridItems} mt={gridTitle ? 8 : 0} width="100%" />
						{link?.title && link?.urlPath && (
							<Button as={Link} to={link.urlPath} mt={5} fontSize="lg">
								{link.title}
							</Button>
						)}
					</Flex>
				</Wrapper>
			);
		}
		case 'embed': {
			const { title, intro, embeds, backgroundColor } = section;
			return (
				<Wrapper heading={title} intro={intro} bg={transformBackgroundColor(backgroundColor)}>
					<Tabs mt={16} mx="auto" isLazy variant="soft-rounded" colorScheme="secondary">
						{embeds.length > 1 && (
							<TabList mb={3} gap={3}>
								{embeds.map((embed) => (
									<Tab key={embed.title}>{embed.title}</Tab>
								))}
							</TabList>
						)}

						<TabPanels>
							{embeds.map((embed) => (
								<TabPanel key={embed.title}>
									<AspectRatio ratio={16 / 10}>
										<iframe src={embed.url} title={embed.title} loading="lazy" />
									</AspectRatio>
								</TabPanel>
							))}
						</TabPanels>
					</Tabs>
				</Wrapper>
			);
		}
		case 'reviews': {
			const { title, intro, subtitle, reviews } = section;

			return reviews && reviews.length > 0 ? (
				<SectionNew
					title={title}
					subtitle={subtitle}
					intro={intro}
					className={'py-16'}
					hasBackground={false}
					id={createId(title)}
				>
					<ReviewsCarousel reviews={reviews} className="mt-12" />
				</SectionNew>
			) : null;
		}
		case 'steps': {
			const { title, intro, steps, backgroundColor } = section;
			return (
				<Wrapper heading={title} intro={intro} bg={transformBackgroundColor(backgroundColor)}>
					<Steps items={steps} className="mt-16" />
				</Wrapper>
			);
		}
		case 'bulletPoints': {
			const { title, subtitle, intro, points, numbered, designElementPosition } = section;
			return (
				<SectionNew
					title={title}
					subtitle={subtitle}
					intro={intro}
					isCentered={designElementPosition === 'none'}
					containerClassName={cn(
						designElementPosition === 'none' && 'py-16',
						designElementPosition === 'left' && 'py-20 lg:pl-80',
						designElementPosition === 'right' && 'py-20 lg:pr-80',
					)}
					id={createId(title)}
				>
					<div className="mt-20 grid grid-cols-1 gap-x-16 gap-y-12 md:grid-cols-2 xl:grid-cols-3">
						{points.map((point, idx) => (
							<React.Fragment key={idx}>
								<div
									className={cn(
										'relative flex gap-8 md:flex-col md:gap-4',
										!numbered && 'relative pt-6',
									)}
								>
									{numbered ? (
										<DiamondNumber number={idx + 1} />
									) : (
										<div
											className={
												"absolute left-0 top-0 -z-10 flex h-16 w-16 shrink-0 items-center justify-center text-2xl font-semibold text-secondary-700 after:absolute after:-left-10 after:top-0 after:block after:h-16 after:w-16 after:rotate-45 after:rounded after:bg-[#E6EFF7] after:opacity-50 after:content-['']"
											}
										/>
									)}
									<div>
										{Boolean(title) && (
											<p className="text-xl font-bold text-secondary-700">{point.title}</p>
										)}
										<MarkdownRaw className="text-lg text-secondary-700">{point.text}</MarkdownRaw>
									</div>
								</div>
							</React.Fragment>
						))}
					</div>
					{designElementPosition && designElementPosition !== 'none' && (
						<div
							className={cn(
								'absolute z-10 hidden h-[800px] w-[800px] overflow-hidden rounded-[50px] lg:block',
								designElementPosition === 'left' && 'left-[-1000px] top-[-200px] rotate-[130deg]',
								designElementPosition === 'right' && 'right-[-1000px] top-[-100px] rotate-[330deg]',
							)}
						>
							<StaticImage
								src="../../../static/images/hero_background.png"
								alt={''}
								className="h-full w-full"
							/>
							<div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white to-white" />
						</div>
					)}
				</SectionNew>
			);
		}
		default:
			throw new Error(`unsupported section type: ${section.type}.`);
	}
};
